import { useEffect, useRef, useState } from 'react'

import { Link as RouterLink, Redirect, useHistory } from 'react-router-dom'

import {
  getExtraProperty,
  hasScopesPermission,
} from '@tribeplatform/gql-client'
import { Form, FormRef } from '@tribeplatform/react-components/Form'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSignup,
  useSpaceUtil,
  useValidateLink,
  useValidateToken,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useRefreshToken } from '../../hooks/useRefreshToken'
import { isMemberNotConfirmed } from '../../lib/app.lib'
import { useQuery } from '../../lib/useQuery'
import { EMAIL_PATTERN } from '../../lib/validator.utils'
import { Ssos } from './Ssos'
import { onAuthenticate } from './utils/onAuthenticate'

export const SignUp = ({ invitationLinkId }) => {
  const [disabledEmail, setDisabledEmail] = useState(false)
  const formRef = useRef<FormRef>()
  const history = useHistory()
  const { data: authToken } = useAuthToken()
  const { network } = authToken
  const { update } = useRefreshToken()
  // get the invitation token if there is any
  const query = useQuery()
  const invitationToken = query.token
  const queryString: { redirect?: string; method?: string } = {}
  if (query.redirect) queryString.redirect = query.redirect
  if (query.method) queryString.method = query.method

  const termsOfServiceUrl =
    getExtraProperty(network?.extraProperties, 'termsOfServiceUrl') ??
    network?.termsOfServiceUrl

  const privacyPolicyUrl =
    getExtraProperty(network?.extraProperties, 'privacyPolicyUrl') ??
    network?.privacyPolicyUrl

  const { generatePath } = useSpaceUtil()

  // validate the invitation link if there is any
  const { error: linkValidationError } = useValidateLink({
    variables: { id: invitationLinkId },
    useQueryOptions: { enabled: !!invitationLinkId, retry: false },
  })

  // validate the token if there is any
  const { data: tokenStatus, error: tokenValidationError } = useValidateToken({
    variables: { token: invitationToken },
    useQueryOptions: { enabled: !!invitationToken, retry: false },
  })

  useEffect(() => {
    if (formRef.current?.methods?.setValue && tokenStatus?.inviteeEmail) {
      formRef.current.methods.setValue('name', tokenStatus.inviteeName)
      formRef.current.methods.setValue('email', tokenStatus.inviteeEmail)
      setDisabledEmail(true)
    }
  }, [formRef, tokenStatus])

  const { mutateAsync: signup, isLoading } = useSignup({
    invitationLinkId,
    invitationToken,
  })

  const [
    canLoginNetwork,
    canJoinNetwork,
    canJoinNetworkWithToken,
    canJoinNetworkWithLink,
  ] = hasScopesPermission(network, [
    'loginNetwork',
    'joinNetwork',
    'joinNetworkWithToken',
    'joinNetworkWithInvitationLink',
  ])

  const onSubmit = async data => {
    delete data.checkbox
    await signup(
      {
        input: data,
      },
      {
        onSuccess: async authToken => {
          const newAuthToken = await update(authToken)
          const { refreshToken, accessToken, member, role } = newAuthToken || {}
          if (accessToken || refreshToken) {
            if (!isMemberNotConfirmed(member, role)) {
              onAuthenticate()
              if (accessToken) {
                window.location.href = '/'
              }
            } else {
              history.replace(
                generatePath(SystemSpaceSlug.VERIFY_EMAIL, null, {
                  memberId: member?.id,
                }),
              )
            }
          }
        },
      },
    )
  }

  // in case of invitation link corruption redirect to generic sign up page
  if (linkValidationError && invitationLinkId) {
    toast({
      description: 'The invitation link has been expired.',
      status: 'error',
    })
    return <Redirect to="/auth/signup" />
  }

  // if token is invalid redirect to generic sign up page
  if (tokenValidationError && invitationToken) {
    toast({
      description: 'The invitation link has been expired.',
      status: 'error',
    })
    return <Redirect to="/auth/signup" />
  }

  if (!canJoinNetwork && !invitationLinkId && !invitationToken) {
    return <Redirect to="/auth/login" />
  }

  if (!canJoinNetwork && !canJoinNetworkWithLink && !canJoinNetworkWithToken) {
    return <Redirect to="/auth/login" />
  }

  const showSignUpForm = !network.hideDefaultAuthenticationForm

  return (
    <>
      <div className="lg:w-1/2 md:w-full flex mx-auto flex-col py-6 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center text-basicMain-900">
          <h2 className="mt-6 text-3xl font-extrabold">Create your account</h2>
          {canLoginNetwork && (
            <div className="mt-3 text-lg">
              Already a member?{' '}
              <Link
                as={RouterLink}
                to={generatePath(SystemSpaceSlug.LOGIN, null, queryString)}
              >
                Log in now
              </Link>
            </div>
          )}
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md flex flex-col space-y-5">
          <Card className="py-8 px-4">
            {showSignUpForm && (
              <Form
                className="space-y-6"
                onSubmit={onSubmit}
                defaultValues={{
                  name: tokenStatus?.inviteeName || '',
                  email: tokenStatus?.inviteeEmail || '',
                }}
                ref={formRef}
              >
                <Form.Input name="name" autoComplete="name" label="Name" />
                <Form.Input
                  disabled={disabledEmail}
                  validation={{
                    required: 'Email is required',
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: 'Invalid email address',
                    },
                  }}
                  name="email"
                  autoComplete="email"
                  label="Email"
                />

                <Form.Input
                  validation={{
                    required: 'Your password must be less than 4 characters.',
                  }}
                  type="password"
                  label="Create a password"
                  name="password"
                  autoComplete="current-password"
                />

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Form.Checkbox
                      name="checkbox"
                      label={
                        <div>
                          By creating an account, I agree to the{' '}
                          {termsOfServiceUrl ? (
                            <Link
                              rel="noopener"
                              target="_blank"
                              href={termsOfServiceUrl}
                            >
                              Terms of Service
                            </Link>
                          ) : (
                            'Terms of Service'
                          )}{' '}
                          and{' '}
                          {privacyPolicyUrl ? (
                            <Link
                              rel="noopener"
                              target="_blank"
                              href={privacyPolicyUrl}
                            >
                              Privacy Policy
                            </Link>
                          ) : (
                            'Privacy Policy'
                          )}
                        </div>
                      }
                      validation={{
                        required:
                          'You must agree with the terms and conditions',
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Button fullWidth type="submit" loading={isLoading} size="lg">
                    Create my account
                  </Button>
                </div>
              </Form>
            )}
            <Ssos
              hasDivider={showSignUpForm}
              invitationLinkId={invitationLinkId}
              invitationToken={invitationToken}
            />
          </Card>
        </div>
      </div>
    </>
  )
}
